import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Checkbox, Stack, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { Address, Company, TravelAgencyContract } from '@mews-commander/data-access';
import { TooltippedSpan } from '@mews-commander/ui';
import { CompaniesFormFields, CompaniesFormValues } from '../../../../../../schemas';

interface CompanyAdditionalProperties {
    address?: Address;
    travelAgencyContract?: TravelAgencyContract;
}

export type CompanyWithAdditionalProperties = Company & CompanyAdditionalProperties;

const columnHelper = createColumnHelper<CompanyWithAdditionalProperties>();

export const useCompaniesTableColumns = ({
    selectedCompanies,
    toggleSelected,
    getAreAllSelected,
    toggleAllSelected,
    companiesData,
}: {
    selectedCompanies: CompaniesFormValues;
    toggleSelected: (props: {
        checked: boolean;
        id: string;
        key: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency;
    }) => void;
    getAreAllSelected: (key: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency) => boolean;
    toggleAllSelected: (
        checked: boolean,
        key: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency
    ) => void;
    companiesData: CompanyWithAdditionalProperties[];
}) =>
    useMemo(
        () => [
            columnHelper.accessor('name', {
                header: () => lt(TextKey.Company),
                cell: (props) => {
                    const address = companiesData.find((company) => company.id === props.row.id)?.address;
                    const { addressLine1, city, countryCode, postalCode } = address || {};

                    return (
                        <TooltippedSpan
                            tooltip={
                                addressLine1 != null &&
                                city != null &&
                                countryCode != null &&
                                postalCode != null && (
                                    <Stack vertical itemAlignment="start">
                                        <Typography
                                            variant="inverse"
                                            textStyle="bodySmall"
                                            as="div"
                                        >{`${addressLine1}${UnicodeEntity.Comma} ${city}${UnicodeEntity.Comma}`}</Typography>
                                        <Typography
                                            variant="inverse"
                                            textStyle="bodySmall"
                                            as="div"
                                        >{`${countryCode}${UnicodeEntity.Comma} ${postalCode}`}</Typography>
                                    </Stack>
                                )
                            }
                        >
                            {props.getValue()}
                        </TooltippedSpan>
                    );
                },
            }),
            columnHelper.accessor('id', {
                header: () => (
                    <Stack itemAlignment="center">
                        <Checkbox
                            disabled={companiesData.length === 0}
                            checked={getAreAllSelected(CompaniesFormFields.LinkAsCompany)}
                            onChange={(_, checked) => toggleAllSelected(checked, CompaniesFormFields.LinkAsCompany)}
                            indeterminate={
                                !getAreAllSelected(CompaniesFormFields.LinkAsCompany) &&
                                companiesData.some((company) => selectedCompanies[company.id]?.linkAsCompany)
                            }
                        />
                        {UnicodeEntity.NoBreakSpace}
                        {lt(TextKey.RoutingRulesFormCompaniesTableColumnLinkAsCompany)}
                    </Stack>
                ),
                cell: (props) => (
                    <Checkbox
                        checked={selectedCompanies[props.row.id]?.linkAsCompany}
                        onChange={(_, checked) =>
                            toggleSelected({ checked, id: props.row.id, key: CompaniesFormFields.LinkAsCompany })
                        }
                    />
                ),
            }),
            columnHelper.accessor('travelAgencyContract', {
                header: () => (
                    <Stack itemAlignment="center">
                        <Checkbox
                            disabled={companiesData.filter((company) => company.travelAgencyContract).length === 0}
                            checked={getAreAllSelected(CompaniesFormFields.LinkAsTravelAgency)}
                            onChange={(_, checked) =>
                                toggleAllSelected(checked, CompaniesFormFields.LinkAsTravelAgency)
                            }
                            indeterminate={
                                !getAreAllSelected(CompaniesFormFields.LinkAsTravelAgency) &&
                                companiesData.some((company) => selectedCompanies[company.id]?.linkAsTravelAgency)
                            }
                        />
                        {UnicodeEntity.NoBreakSpace}
                        {lt(TextKey.RoutingRulesFormCompaniesTableColumnLinkAsTravelAgency)}
                    </Stack>
                ),
                cell: (props) => {
                    if (props.getValue() == null) {
                        return null;
                    }
                    return (
                        <Checkbox
                            checked={selectedCompanies[props.row.id]?.linkAsTravelAgency}
                            onChange={(_, checked) =>
                                toggleSelected({
                                    checked,
                                    id: props.row.id,
                                    key: CompaniesFormFields.LinkAsTravelAgency,
                                })
                            }
                        />
                    );
                },
            }),
        ],
        [getAreAllSelected, toggleAllSelected, companiesData, selectedCompanies, toggleSelected]
    );
